/**
 * SHEET CONTROLLER (B10) — the mobile bottom sheet's hands. Plain DOM, no React:
 * per-frame values go straight to the element (transform) on the shared ticker.
 *
 *  - The sheet box is `full + safe` tall and rides on translateY(full − h), where h
 *    is the visible height above the safe area. Moving it never lays anything out.
 *  - The content's bottom edge (--ld-loc-layout-sheet-pad) follows the settled
 *    height, so a list scrolls exactly to the viewport's bottom. It is opened to
 *    full before a move up (content already there) and closed after a move down;
 *    during a drag it opens only once the sheet rises past it (no layout otherwise).
 *  - Drag: pointer events on the sheet + window; a non-passive touchmove stops the
 *    browser from scrolling the list once the gesture belongs to the sheet.
 *    Release: 0.45 px/ms moves one snap in the drag direction, else the nearest (§A.4).
 *  - A tap that ends a drag never clicks through.
 */
import type { SheetSnap } from "@/contracts/store";
import { EASE } from "@/design";
import { tween } from "@/lib/ticker";
import { decideDragIntent, releaseVelocity, resolveSnap, rubberBand, sheetCover, sheetMotion, SHEET, type DragIntent, type DragSample } from "./layoutMath";

export interface SheetGeometry {
  heights: Record<SheetSnap, number>;
  /** env(safe-area-inset-bottom). */
  safe: number;
}

export interface SheetControllerOptions {
  /** The user released a drag on this snap. */
  onRelease: (snap: SheetSnap) => void;
  /**
   * Every frame: the visible height in px (safe area included) and how much of the
   * map the sheet covers between half (0) and full (1), for chrome that rides on it.
   */
  onHeight?: (visiblePx: number, cover: number) => void;
  /** The snap the store holds now. */
  snap: () => SheetSnap;
  reduced: () => boolean;
  /** false while the sheet is hidden, dimmed for a drive or asleep. */
  enabled: () => boolean;
}

/** Height that puts the whole box, shadow included, below the viewport. */
export const OFFSCREEN = -96;

interface Drag {
  id: number;
  x0: number;
  y0: number;
  h0: number;
  zone: "handle" | "content";
  scroller: HTMLElement | null;
  intent: DragIntent;
  samples: DragSample[];
}

function nearestScroller(from: EventTarget | null, stop: HTMLElement): HTMLElement | null {
  let el = from instanceof Element ? (from as HTMLElement) : null;
  while (el && el !== stop) {
    const cs = getComputedStyle(el);
    if ((cs.overflowY === "auto" || cs.overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1) return el;
    el = el.parentElement;
  }
  return null;
}

export class SheetController {
  private h = Number.NaN;
  private target = Number.NaN;
  private abort: AbortController | null = null;
  private drag: Drag | null = null;
  private suppressUntil = 0;
  /** The content's bottom edge, as the distance from the box's full height (px). */
  private pad = -1;

  constructor(
    private readonly el: HTMLElement,
    private geo: SheetGeometry,
    private readonly opts: SheetControllerOptions,
  ) {
    el.addEventListener("pointerdown", this.onDown);
    el.addEventListener("touchmove", this.onTouchMove, { passive: false });
    el.addEventListener("click", this.onClickCapture, true);
  }

  destroy(): void {
    this.abort?.abort();
    this.endTracking();
    this.el.removeEventListener("pointerdown", this.onDown);
    this.el.removeEventListener("touchmove", this.onTouchMove);
    this.el.removeEventListener("click", this.onClickCapture, true);
  }

  get height(): number {
    return this.h;
  }

  get dragging(): boolean {
    return this.drag?.intent === "drag";
  }

  setGeometry(geo: SheetGeometry): void {
    this.geo = geo;
    if (Number.isFinite(this.h)) this.write(this.h);
  }

  /** Moves the sheet to `h` (visible px above the safe area). Animated moves use §E.9 timing. */
  goTo(h: number, how: "animate" | "jump"): void {
    if (this.dragging) return; // the finger wins; release decides
    if (h === this.target && this.abort && !this.abort.signal.aborted) return; // already on its way
    this.abort?.abort();
    this.abort = null;
    this.target = h;
    const from = this.h;
    const full = this.geo.heights.full;
    if (how === "jump" || !Number.isFinite(from) || Math.abs(h - from) < 0.5) {
      this.write(h);
      this.setPad(full - Math.max(h, 0));
      return;
    }
    if (h > from) this.setPad(full - h); // content is in place before it comes into view
    const { ms, ease } = sheetMotion(from, h, this.opts.reduced());
    const ac = new AbortController();
    this.abort = ac;
    void tween(ms, EASE[ease], (e) => this.write(from + (h - from) * e), { signal: ac.signal }).then((done) => {
      if (!done) return;
      if (this.abort === ac) this.abort = null;
      this.setPad(full - Math.max(h, 0));
    });
  }

  /* ── rendering ───────────────────────────────────────────────── */

  private write(h: number): void {
    this.h = h;
    this.el.style.transform = `translate3d(0,${Math.round((this.geo.heights.full - h) * 10) / 10}px,0)`;
    this.opts.onHeight?.(Math.max(0, h + this.geo.safe), sheetCover(h, this.geo.heights));
  }

  private setPad(px: number): void {
    const v = Math.max(0, Math.round(px));
    if (v === this.pad) return;
    this.pad = v;
    this.el.style.setProperty("--ld-loc-layout-sheet-pad", `${v}px`);
  }

  /* ── drag ────────────────────────────────────────────────────── */

  private onDown = (e: PointerEvent): void => {
    if (!this.opts.enabled() || this.drag) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const t = e.target instanceof Element ? e.target : null;
    const zone = t?.closest("[data-ld-sheet-handle],[data-ld-sheet-header]") ? "handle" : "content";
    if (t?.closest("[data-ld-sheet-nodrag]")) return;
    this.drag = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      h0: this.h,
      zone,
      scroller: nearestScroller(e.target, this.el),
      intent: "pending",
      samples: [],
    };
    window.addEventListener("pointermove", this.onMove, { passive: false });
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("pointercancel", this.onCancel);
  };

  /** Decides (once) whether this gesture moves the sheet. `t` = the event's timeStamp. */
  private consider(x: number, y: number, t: number): DragIntent {
    const d = this.drag;
    if (!d) return "native";
    if (d.intent !== "pending") return d.intent;
    d.intent = decideDragIntent({
      dx: x - d.x0,
      dy: y - d.y0,
      zone: d.zone,
      snap: this.opts.snap(),
      scrollTop: d.scroller ? d.scroller.scrollTop : null,
    });
    if (d.intent === "drag") {
      this.abort?.abort();
      this.abort = null;
      d.h0 = this.h;
      d.y0 = y; // no jump by the slop distance
      d.samples = [{ t, h: this.h }]; // the gesture's start counts for the release velocity
      this.el.classList.add("is-dragging");
      try {
        this.el.setPointerCapture(d.id);
      } catch {
        /* the pointer may already be gone */
      }
    } else if (d.intent === "native") {
      this.endTracking();
    }
    return d.intent;
  }

  private onMove = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    if (this.consider(e.clientX, e.clientY, e.timeStamp) !== "drag") return;
    e.preventDefault();
    const h = rubberBand(d.h0 - (e.clientY - d.y0), this.geo.heights);
    // Rising past the content's settled edge: open it to full once (one layout), never on the way down.
    if (h > this.geo.heights.full - this.pad) this.setPad(0);
    this.write(h);
    d.samples.push({ t: e.timeStamp, h });
    if (d.samples.length > 24) d.samples.splice(0, d.samples.length - 24);
  };

  private onTouchMove = (e: TouchEvent): void => {
    const d = this.drag;
    if (!d) return;
    const touch = e.touches[0];
    if (d.intent === "pending" && touch) this.consider(touch.clientX, touch.clientY, e.timeStamp);
    if (this.drag?.intent === "drag" && e.cancelable) e.preventDefault();
  };

  private onUp = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    const wasDrag = d.intent === "drag";
    const v = wasDrag ? releaseVelocity(d.samples, e.timeStamp, SHEET.velocityWindow) : 0;
    this.endTracking();
    if (wasDrag) this.release(v);
  };

  private onCancel = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    const wasDrag = d.intent === "drag";
    this.endTracking();
    if (wasDrag) this.release(0);
  };

  private release(velocity: number): void {
    const snap = resolveSnap(this.h, velocity, this.geo.heights);
    this.suppressUntil = performance.now() + 350;
    this.target = Number.NaN;
    this.goTo(this.geo.heights[snap], "animate");
    this.opts.onRelease(snap);
  }

  private endTracking(): void {
    const d = this.drag;
    this.drag = null;
    this.el.classList.remove("is-dragging");
    if (d) {
      try {
        if (this.el.hasPointerCapture(d.id)) this.el.releasePointerCapture(d.id);
      } catch {
        /* already released */
      }
    }
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onCancel);
  }

  private onClickCapture = (e: MouseEvent): void => {
    if (performance.now() < this.suppressUntil) {
      e.stopPropagation();
      e.preventDefault();
    }
  };
}
